// Die einzigen Stellen, an denen in der Oberfläche formatiert wird (design-system §6.4). Alle bauen auf
// shared/format.ts; hier kommen nur Schrift (Tabellenziffern, Mono), Leerwert und Zugänglichkeit dazu.
import { ArrowRight, Check, Copy } from 'lucide-react'
import { useState, type ReactNode } from 'react'
import {
  EMPTY,
  formatDate,
  formatDateTime,
  formatEuro,
  formatFullTimestamp,
  formatMonth,
  formatPercent,
  formatQty,
  formatRelative,
  formatTime,
  type EuroOptions,
} from '@shared/format'
import type { Cents } from '@shared/money'
import type { Qty as QtyValue } from '@shared/qty'
import type { IsoDate } from '@shared/time'
import { findUnit, unitLabel, unitShort } from '@shared/units'
import { cn } from '../lib/cn'

/** Leerer Wert: Halbgeviertstrich in fg-disabled – nie „0“ für „unbekannt“. */
export function EmptyValue({ className }: { className?: string }) {
  return (
    <span className={cn('text-fg-disabled', className)} aria-label="kein Wert">
      {EMPTY}
    </span>
  )
}

export interface MoneyProps extends EuroOptions {
  cents: Cents | null | undefined
  /** negative Werte in danger-fg – nur, wenn der negative Wert ein Problem ist */
  flagNegative?: boolean
  className?: string
}

/** Geld: „1.234,50 €“, negativ mit echtem Minus. */
export function Money({ cents, flagNegative, className, ...options }: MoneyProps) {
  if (cents === null || cents === undefined) return <EmptyValue className={className} />
  return (
    <span className={cn('tabular-nums whitespace-nowrap', flagNegative && cents < 0 && 'text-danger-fg', className)}>
      {formatEuro(cents, options)}
    </span>
  )
}

export interface QtyProps {
  qty: QtyValue | null | undefined
  /** Schlüssel aus dem Einheiten-Katalog ('crate') oder eine freie Kurzform */
  unit?: string
  minDecimals?: number
  className?: string
}

/** Menge mit Einheit: „46 Ka“. Für Screenreader ausgeschrieben („46 Kästen“). */
export function Qty({ qty, unit, minDecimals, className }: QtyProps) {
  if (qty === null || qty === undefined) return <EmptyValue className={className} />
  const known = unit ? findUnit(unit) : undefined
  const short = unit ? (known ? unitShort(unit) : unit) : undefined
  const text = formatQty(qty, { unit: short, minDecimals })
  const spoken = known ? `${formatQty(qty, { minDecimals })} ${unitLabel(known.code, qty / 1000)}` : undefined
  return (
    <span className={cn('tabular-nums whitespace-nowrap', className)} aria-label={spoken}>
      {text}
    </span>
  )
}

/** Prozent aus Basispunkten: 3140 → „31,4 %“. */
export function Percent({
  basisPoints,
  digits,
  className,
}: {
  basisPoints: number | null | undefined
  digits?: number
  className?: string
}) {
  if (basisPoints === null || basisPoints === undefined) return <EmptyValue className={className} />
  return <span className={cn('tabular-nums whitespace-nowrap', className)}>{formatPercent(basisPoints, digits)}</span>
}

export interface DateTextProps {
  /** Kalendertag 'YYYY-MM-DD', Zeitpunkt (Date) oder ISO-Zeitstempel */
  value: IsoDate | Date | string | null | undefined
  /** 'date' (Standard) · 'datetime' · 'time' · 'relative' (nur in der letzten Stunde relativ) · 'month' (MHD 03.2027) */
  format?: 'date' | 'datetime' | 'time' | 'relative' | 'month'
  weekday?: boolean
  /** enge Spalten: ohne Jahr im laufenden Jahr */
  short?: boolean
  className?: string
}

const ISO_DAY = /^\d{4}-\d{2}-\d{2}$/

/** Datum und Zeit in Europe/Berlin. Zeitpunkte tragen das volle Datum im Tooltip. */
export function DateText({ value, format = 'date', weekday, short, className }: DateTextProps) {
  if (value === null || value === undefined || value === '') return <EmptyValue className={className} />
  if (typeof value === 'string' && ISO_DAY.test(value)) {
    const text = format === 'month' ? formatMonth(value) : formatDate(value, { weekday, short })
    return (
      <time dateTime={value} className={cn('tabular-nums whitespace-nowrap', className)}>
        {text}
      </time>
    )
  }
  const at = value instanceof Date ? value : new Date(value)
  if (Number.isNaN(at.getTime())) return <EmptyValue className={className} />
  const text =
    format === 'time'
      ? formatTime(at)
      : format === 'datetime'
        ? formatDateTime(at, { short })
        : format === 'relative'
          ? formatRelative(at)
          : formatDate(at, { weekday, short })
  return (
    <time
      dateTime={at.toISOString()}
      title={formatFullTimestamp(at)}
      className={cn('tabular-nums whitespace-nowrap', className)}
    >
      {text}
    </time>
  )
}

export async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text)
    return true
  } catch {
    return false
  }
}

/** Kennung (Artikelnummer, EAN, Charge, Belegnummer): Mono, nie mit Tausenderpunkt, auf Wunsch kopierbar. */
export function CodeText({
  children,
  copyable,
  muted = true,
  className,
}: {
  children: string | null | undefined
  copyable?: boolean
  muted?: boolean
  className?: string
}) {
  const [copied, setCopied] = useState(false)
  if (!children) return <EmptyValue className={className} />
  const code = <span className={cn('code-id', muted && 'text-fg-muted', className)}>{children}</span>
  if (!copyable) return code
  return (
    <span className="group/code inline-flex items-center gap-1">
      {code}
      <button
        type="button"
        aria-label={copied ? 'Kopiert' : `${children} kopieren`}
        onClick={(event) => {
          event.stopPropagation()
          void copyText(children).then((ok) => {
            setCopied(ok)
            if (ok) setTimeout(() => setCopied(false), 1500)
          })
        }}
        className="flex size-5 items-center justify-center rounded-xs text-fg-subtle opacity-0 hit-area hover:bg-hover hover:text-fg focus-visible:opacity-100 group-hover/code:opacity-100 touch:opacity-100"
      >
        {copied ? (
          <Check className="size-3.5 text-success-fg" strokeWidth={2} aria-hidden />
        ) : (
          <Copy className="size-3.5" strokeWidth={1.75} aria-hidden />
        )}
      </button>
    </span>
  )
}

/** Vorher → Nachher: alter Wert durchgestrichen, neuer Wert betont. Benutzt von Verlauf, Vorschau, Bestätigung. */
export function DiffValue({
  before,
  after,
  layout = 'inline',
  className,
}: {
  before?: ReactNode
  after: ReactNode
  layout?: 'inline' | 'arrow'
  className?: string
}) {
  const hasBefore = before !== undefined && before !== null && before !== ''
  return (
    <span className={cn('inline-flex items-baseline gap-1.5 whitespace-nowrap', className)}>
      {hasBefore && (
        <>
          <del className="text-fg-subtle line-through">{before}</del>
          {layout === 'arrow' && (
            <ArrowRight
              className="size-3.5 shrink-0 self-center text-fg-subtle"
              strokeWidth={1.75}
              aria-label="wird zu"
            />
          )}
        </>
      )}
      <ins className="font-medium text-fg no-underline">{after}</ins>
    </span>
  )
}
